import { Box, Typography } from "@mui/material";
import { ReactElement, useContext } from "react";

import { ReservationBrowsingPeriod, Mode } from "models/Mode";
import { Reservation } from "models/Reservation";
import { getHoursFormatted } from "modules/reservations/helpers/date/getHoursFormatted";

import { ReservationOptionIcon } from "../ReservationOptionIcon";
import { ReservationsContext } from "../Reservations";


interface ReservationsInfoProps {
    ml: number,
    mr: number,
    onReservationClick: (id: string) => void,
    reservations: Array<Reservation>,
    wrapInfo: boolean
}

export function ReservationsInfo({ ml, mr, onReservationClick, reservations, wrapInfo }: ReservationsInfoProps): ReactElement {
    const {
        reservationsControl: {
            browsingPeriod,
            mode,
        },
    } = useContext(ReservationsContext);

    function getFontSize(): number {
        switch (mode) {
            case Mode.RESERVATION_BROWSING:
            case Mode.RESERVATION_PREVIEW:
            case Mode.RESERVATION_CREATION:
            case Mode.RESERVATION_EDIT:
                switch (browsingPeriod) {
                    case ReservationBrowsingPeriod.SELECTED_HOUR:
                        return 11.2;

                    default:
                        return 9;
                }

            case Mode.PRINTING:
                return 12;

            default:
                return 11.2;
        }
    }

    function getLineHeight(): number {
        switch (mode) {
            case Mode.RESERVATION_BROWSING:
            case Mode.RESERVATION_PREVIEW:
            case Mode.RESERVATION_CREATION:
            case Mode.RESERVATION_EDIT:
                switch (browsingPeriod) {
                    case ReservationBrowsingPeriod.SELECTED_HOUR:
                        return 1.3;

                    default:
                        return 1.0;
                }

            default:
                return 1.3;
        }
    }

    function getInfo(text: ReactElement | string, fontWeight: string, shouldWrap = false): ReactElement {
        const sxWrap = { WebkitBoxOrient: "vertical", WebkitLineClamp: 2, display: "-webkit-box", fontWeight, overflow: "hidden" };
        const sxNoWrap = { fontWeight, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

        return (
            <Typography
                fontSize={getFontSize()}
                sx={{
                    ...shouldWrap ? sxWrap : sxNoWrap,
                    lineHeight: getLineHeight(),
                }}
            >
                { text }
            </Typography>
        );
    }

    return (
        <Box sx={{
            "&::-webkit-scrollbar": { display: "none" },
            "@media print": { maxHeight: "unset" },
            maxWidth: "100%",
            ml,
            mr,
            overflow: "scroll",
        }}>
            {
                reservations.map(({ amountOfPeople, end, id, inGarden, instagram, isConfirmed, isFirstTime, isRegularGuest, name, showable, start, thisTable }) => {
                    const reservationInfo: string = `${ getHoursFormatted(start, end) } (${ amountOfPeople })`;
                    const guestInfo: ReactElement =
                        <>
                            { isFirstTime ? <ReservationOptionIcon field={"isFirstTime"} /> : <></> }
                            { thisTable ? <ReservationOptionIcon field={"thisTable"} /> : <></> }
                            { inGarden ? <ReservationOptionIcon field={"inGarden"} /> : <></> }
                            { isRegularGuest ? <ReservationOptionIcon field={"isRegularGuest"} /> : <></> }
                            { isConfirmed ? <ReservationOptionIcon field={"isConfirmed"} /> : <></> }
                            { (name || `@${ instagram }`).toUpperCase() }
                        </>
                    ;

                    if (typeof showable !== "undefined" && !showable) {
                        return null;
                    }

                    return (
                        <Box
                            key={id}
                            sx={{ mb: 0.35 }}
                            onClick={() => onReservationClick(id)}
                        >
                            { getInfo(reservationInfo, "bold", wrapInfo) }
                            { getInfo(guestInfo, "normal", wrapInfo) }
                        </Box>
                    );
                })
            }
        </Box>);
}